import { FC, useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { Chip } from "@mui/material";
import Module from "@/components/module/Module";
import { ortexColorState, positiveState, negativeState, neutralState, textColorState } from "@/lib/store";
import { EarningsFinancials, EventListRow } from "@/models/event-page";
import { eventDetail, eventStatus, formatEps, isEarningsRow, relativeFromNow } from "./helpers";
import dayjs from "dayjs";
import { eventSelectionKey } from "@/lib/event-link";
import styles from "./EventPageRevamp.module.scss";

interface EventsRailProps {
    events: EventListRow[] | null | undefined;
    financialsByEvent: Record<number, EarningsFinancials> | null | undefined;
    selectedKey: string | null;
    onSelect: (key: string) => void;
    /** Scroll the rail to this event once it renders (a linked event, on load). */
    scrollToKey?: string | null;
    loading?: boolean;
}

/**
 * Left rail listing every event (earnings + others). Each item shows the type,
 * date, relative time, a status dot (reported / confirmed / estimated), a
 * transcript indicator, and — for earnings — the EPS est/act and beat/miss chip.
 */
const EventsRail: FC<EventsRailProps> = ({ events, financialsByEvent, selectedKey, onSelect, scrollToKey, loading }) => {
    const safeEvents = events ?? [];
    // Newest / upcoming events at the top; older history scrolls below.
    const sortedEvents = [...safeEvents].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
    const safeFinancials = financialsByEvent ?? {};
    const accent = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);
    const textColor = useAtomValue(textColorState);
    const listRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (!scrollToKey || loading) return;
        const list = listRef.current;
        const button = Array.from(list?.querySelectorAll<HTMLElement>("[data-event-key]") ?? []).find((el) => el.dataset.eventKey === scrollToKey);
        if (!list || !button) return;
        // Center it in the rail list, which scrolls on its own, so the page stays put
        // and the event detail beside the rail stays in view.
        const offset = button.getBoundingClientRect().top - list.getBoundingClientRect().top;
        list.scrollTop += offset - (list.clientHeight - button.offsetHeight) / 2;
    }, [scrollToKey, loading]);

    const statusColor = (status: string): string => {
        if (status === "confirmed") return accent;
        if (status === "estimated") return neutral;
        return textColor;
    };

    return (
        <Module title="Upcoming & Past Events" noPadding={{ source: "body", type: "all" }}>
            <div className={styles.rail}>
                <div className={styles.railHeader}>
                    <span className={styles.railLabel}>{safeEvents.length} events</span>
                </div>
                {loading ? (
                    <div className={styles.loading}>Loading events…</div>
                ) : safeEvents.length === 0 ? (
                    <div className={styles.empty}>No events for this company.</div>
                ) : (
                    <nav ref={listRef} className={styles.railList} aria-label="Company events">
                        {sortedEvents.map((e) => {
                            const status = eventStatus(e);
                            const isEarnings = isEarningsRow(e);
                            const fin = safeFinancials[e.id];
                            const epsBlock = fin?.eps;
                            const result = epsBlock?.result ?? null;
                            const day = e.date ? dayjs(e.date).format("DD") : "—";
                            const month = e.date ? dayjs(e.date).format("MMM") : "";
                            // Show the year only when it differs from the current (local) year,
                            // so the date box isn't unbalanced on recent events.
                            const eventYear = e.date ? dayjs(e.date).year() : null;
                            const showYear = eventYear != null && eventYear !== dayjs().year();
                            // "0.26 USD" (dividend) / "4:1" (split); null on every other row.
                            const detail = eventDetail(e);
                            const key = eventSelectionKey(e);

                            return (
                                <button
                                    key={key}
                                    data-event-key={key}
                                    aria-current={key === selectedKey ? "true" : undefined}
                                    className={`${styles.eventBtn} ${isEarnings ? styles.eventBtnEarnings : ""} ${key === selectedKey ? styles.selected : ""}`}
                                    onClick={() => onSelect(key)}
                                >
                                    <div className={styles.eventDateBox}>
                                        {e.date ? (
                                            <time dateTime={new Date(e.date).toISOString()}>
                                                <span className={styles.eventDay}>{day}</span>
                                                <span className={styles.eventMonth}>{month}</span>
                                                {showYear && <span className={styles.eventYear}>{eventYear}</span>}
                                            </time>
                                        ) : (
                                            <span className={styles.eventDay}>{day}</span>
                                        )}
                                    </div>
                                    <div className={styles.eventMain}>
                                        <div className={styles.eventTitle}>{e.title || "Event"}</div>
                                        <div className={styles.eventTags}>
                                            <span className={styles.eventType}>{e.type || ""}</span>
                                            {detail && <span className={styles.eventDetail}>{detail}</span>}
                                            {e.hasTranscripts && <Chip label="Transcript" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, color: accent, borderColor: accent }} />}
                                            {result === "beat" && <Chip label="Beat" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: positive, backgroundColor: `${positive}29` }} />}
                                            {result === "miss" && <Chip label="Miss" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: negative, backgroundColor: `${negative}29` }} />}
                                            {result === "inline" && <Chip label="Inline" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: textColor }} />}
                                        </div>
                                        {isEarnings && epsBlock && (epsBlock.actual != null || epsBlock.estimate != null) && (
                                            <div className={styles.epsRow}>
                                                {epsBlock.estimate != null && (
                                                    <span>
                                                        est <b>{formatEps(epsBlock.estimate)}</b>
                                                    </span>
                                                )}
                                                {epsBlock.actual != null && (
                                                    <span>
                                                        act <b>{formatEps(epsBlock.actual)}</b>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.eventStatus}>
                                        <span className={styles.eventRel}>{relativeFromNow(e.date)}</span>
                                        <span className={styles.statusDot} style={{ background: statusColor(status) }} />
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                )}
            </div>
        </Module>
    );
};

export default EventsRail;
